/**
 * Helpers used to update the page metadata.
 *
 * Use ?devflagk=metadata to enable logging.
 */
import Log from "@khan/logging";
import {hasDevFlag, RequestInfo} from "@khan/shared-core";
import {staticUrl, toCanonicalUrl} from "@khan/urls";
import {escapeEntities} from "@khan/util-escape-entities";

import type {PageSettings} from "#data-access/page-settings.ts";

// sync-start:fb-app-id 996150246 ./libs/auth/facebook/src/facebook-util.ts
const FB_APP_ID = "160249463991765";
// sync-end:fb-app-id

// Track which types of meta tags have been appended to the page
const hasAppendedMeta = new Map<string, boolean>();

/**
 * Clear the list of appended meta tags. Used for testing.
 */
export const clearAppendedMeta = () => {
    hasAppendedMeta.clear();
};

/**
 * Append meta tags to the page header, but only once per type.
 *
 * @param id a unique identifier for the meta tags
 * @param tags the HTML tags to append
 */
export const appendMetaTags = (
    id: string,
    tags: string,
    {
        warn = true,
        replace,
    }: {
        warn?: boolean;
        replace?: {
            name?: Array<string>;
            property?: Array<string>;
            rel?: Array<string>;
        };
    } = {},
) => {
    const logging = hasDevFlag("metadata");

    // Make sure we only append meta tags of a specific type once
    if (!replace && hasAppendedMeta.has(id)) {
        if (warn && logging) {
            Log.log(`Meta tags of type ${id} already appended.`, {
                tags,
            });
        }
        return;
    }

    if (replace) {
        // Remove any existing meta tags that we are replacing
        const meta = document.querySelectorAll("meta");
        for (const element of meta) {
            if (
                replace.name?.includes(element.name) ||
                replace.property?.includes(element.getAttribute("property")!)
            ) {
                element.remove();
            }
        }

        // Remove any existing link tags that we are replacing
        if (replace.rel) {
            const link = document.querySelectorAll("link");
            for (const element of link) {
                if (replace.rel.includes(element.rel)) {
                    element.remove();
                }
            }
        }
    }

    // Insert all meta tags into the page header
    const head = document.querySelector("head");
    if (head) {
        head.insertAdjacentHTML("beforeend", tags);
        hasAppendedMeta.set(id, true);
        if (logging) {
            Log.log(`${id} meta tags appended.`, {
                tags,
            });
        }
    }
};

/**
 * Breadcrumbs block.
 */
type Breadcrumb = {
    href: string;
    title: string;
};

/**
 * Returns a breadcrumb trail.
 *
 * A breadcrumb trail on a page indicates the page's position in the site
 * hierarchy.
 *
 * @param {Breadcrumb} breadcrumb The breadcrumb information.
 * @param {string} breadcrumb.href The title of the breadcrumb displayed for the
 * user.
 * @param {string} breadcrumb.title The URL to the webpage that represents the
 * breadcrumb.
 * @param {number} index The position of the breadcrumb in the breadcrumb trail.
 * @returns {object} Object representing a ListItem.
 *
 * @see https://developers.google.com/search/docs/data-types/breadcrumb#list-item
 */
function getBreadcrumb(breadcrumb: Breadcrumb, index: number) {
    return {
        "@type": "ListItem",
        position: index,
        item: {
            "@id": toCanonicalUrl(breadcrumb.href),
            name: breadcrumb.title,
        },
    };
}

/**
 * Sets the markup in the body of the rendered page to categorize the
 * information from the page in search results.
 *
 * @param {Array<Breadcrumb>} breadcrumbsForGoogle The list of breadcrumb trails.
 * @returns {string} The script used by Google to categorize search results.
 *
 * @see https://developers.google.com/search/docs/data-types/breadcrumb#breadcrumb-list
 */
export const setBreadcrumbsForGoogle = ({
    breadcrumbsForGoogle,
}: Readonly<PageSettings>) => {
    if (!breadcrumbsForGoogle || !breadcrumbsForGoogle.length) {
        return;
    }

    const breadcrumbsList = breadcrumbsForGoogle.map(getBreadcrumb);
    // If we pass in breadcrumb data (for google search results), use it.
    // https://developers.google.com/structured-data/breadcrumbs
    appendMetaTags(
        "breadcrumbs",
        `<script type="application/ld+json">
        {
            "@context": "http://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": ${JSON.stringify(breadcrumbsList)}
        }
        </script>`,
    );
};

/**
 * Sets the robots meta tag.
 *
 * The robots meta tag lets you utilize a granular, page-specific approach to
 * controlling how an individual page should be indexed and served to users in
 * search engines.
 *
 * @param {boolean} pageSettings.noIndex Whether we do not want to index this
 * page.
 * @param {boolean} pageSettings.noFollow Whether we do not want to follow links
 * on this page.
 * @returns {string} The specific meta tag that will be used in the rendered
 * page.
 *
 * @see
 * https://developers.google.com/search/reference/robots_meta_tag#directives_1
 */
export const setRobots = ({noIndex, noFollow}: Readonly<PageSettings>) => {
    const {usePreviewFMS, kaLocale} = RequestInfo;
    // Do not index and do not follow links on this page.
    if ((noIndex && noFollow) || usePreviewFMS || kaLocale === "lol") {
        appendMetaTags(
            "robots",
            '<meta name="robots" content="noindex, nofollow" />',
        );

        // Do not index this page, only follow the links on this page.
    } else if (noIndex && !noFollow) {
        appendMetaTags("robots", '<meta name="robots" content="noindex" />');
    }
};

/**
 * Social meta tags.
 *
 * NOTE(john): We should move this into the page builder logic, instead.
 */
export const setSocialTags = () => {
    appendMetaTags(
        "socialTags",
        `<meta property="fb:app_id" content="${FB_APP_ID}"/>
        <meta property="fb:admins" content="100000121497109,1305017,100001572496923"/>
        <meta property="og:site_name" content="Khan Academy"/>
        <meta property="og:type" content="non_profit" />
        <meta property="fb:page_id" content="159403248441" />
        <meta name="twitter:site" content="@khanacademy" />
        <meta name="twitter:card" content="summary_large_image" />`,
        {warn: false},
    );
};

/**
 * Social media video links.
 *
 * @param {string} pageSettings.videoUrl e.g. http://www.youtube.com/v/xxx
 * @param {string} pageSettings.videoEmbedUrl e.g.
 * http://www.youtube.com/embed/xxx
 * @returns {string} The meta tags used to reference videos on socia media.
 *
 * @see https://developers.facebook.com/docs/sharing/webmasters/#media
 */
export const setVideo = ({videoUrl, videoEmbedUrl}: Readonly<PageSettings>) => {
    if (videoUrl) {
        appendMetaTags(
            "videoUrl",
            `<meta property="og:type" content="video.other" />
            <meta property="og:video" content="${videoUrl}" />
            <meta property="og:video:type" content="application/x-shockwave-flash" />`,
        );
    }

    if (videoEmbedUrl) {
        appendMetaTags(
            "videoEmbedUrl",
            `<meta property="og:type" content="video.other" />
            <link rel="video_src" href="${videoEmbedUrl}" />
            <meta name="video_type" content="application/x-shockwave-flash" />`,
        );
    }
};

/**
 * Social media image tags.
 *
 * @param {string} pageSettings.imageUrl
 * @returns {string} The meta tags used to reference images on socia media.
 *
 * @see https://developers.facebook.com/docs/sharing/webmasters/#media
 * @see https://developer.twitter.com/en/docs/tweets/optimize-with-cards/overview/summary-card-with-large-image
 */
export const setImage = ({imageUrl}: Readonly<PageSettings>) => {
    if (imageUrl) {
        return appendMetaTags(
            "imageUrl",
            `<meta property="og:image" content="${imageUrl}" />
            <link rel="image_src" href="${imageUrl}" />
            <meta name="twitter:image" content="${imageUrl}" />
            <meta name="thumbnail" content="${imageUrl}" />`,
            {
                replace: {
                    name: ["thumbnail", "twitter:image"],
                    property: ["og:image"],
                    rel: ["image_src"],
                },
            },
        );
    }

    const defaultImageUrl = staticUrl(
        "/images/khan-logo-dark-background-2.png",
    );
    appendMetaTags(
        "imageUrl",
        `<meta property="og:image:width" content="381" />
        <meta property="og:image:height" content="200" />
        <meta property="og:image" content="${defaultImageUrl}" />
        <meta name="twitter:image" content="${defaultImageUrl}" />`,
        {warn: false},
    );
};

/**
 * Set the page title.
 *
 * We use this as an opportunity to update the live page title (visible to
 * the user in the Browser chrome).
 */
export const setTitle = ({
    showTitleNotification,
    fullTitle,
}: Readonly<PageSettings>) => {
    // Need to make sure that any double-quotes in these strings are
    // escaped.
    const encodedFullTitle = escapeEntities(fullTitle).trim();

    const docTitle = showTitleNotification ? `* ${fullTitle}` : fullTitle;

    if (document.title !== docTitle) {
        document.title = docTitle;

        appendMetaTags(
            "title",
            `<meta name="title" content="${encodedFullTitle}" />
            <meta property="og:title" content="${encodedFullTitle}" />
            <meta name="twitter:title" content="${encodedFullTitle}" />`,
            {
                replace: {
                    name: ["title", "twitter:title"],
                    property: ["og:title"],
                },
            },
        );

        if (hasDevFlag("metadata")) {
            Log.log(`title updated`, {
                title: docTitle,
            });
        }
    }
};

/**
 * Set the page description.
 */
export const setDescription = ({description}: Readonly<PageSettings>) => {
    // Need to make sure that any double-quotes in these strings are
    // escaped.
    const encodedDescription = escapeEntities(description).trim();

    if (encodedDescription) {
        appendMetaTags(
            "desc",
            `<meta name="description" content="${encodedDescription}" />
            <meta property="og:description" content="${encodedDescription}" />
            <meta name="twitter:description" content="${encodedDescription}" />`,
            {
                replace: {
                    name: ["description", "twitter:description"],
                    property: ["og:description"],
                },
            },
        );
    }
};

/**
 * Set all the page meta tags based on the page settings.
 */
export const setPageMetadata = (pageSettings: Readonly<PageSettings>) => {
    setTitle(pageSettings);
    setDescription(pageSettings);
    setRobots(pageSettings);
    setSocialTags();
    setVideo(pageSettings);
    setImage(pageSettings);
    setBreadcrumbsForGoogle(pageSettings);
};
